/**
 * PURPOSE: Cliente del Centro de Programación (FTR-0028).
 * RESPONSIBILITIES: El estado de los flags (WG1) y, desde WG3, TODAS las operaciones administrativas
 *   del módulo: contexto, borrador, sincronización, programación, materialización, participantes,
 *   reorganización, propiedad y canchas.
 *
 *   FAIL CLOSED en el estado: ante cualquier error — red caída, 404 porque el backend aún no tiene el
 *   módulo, respuesta inesperada — se asume TODO apagado. Peor un botón que no aparece que un botón
 *   que lleva a un 404. Y es solo una pista de interfaz: el módulo administrativo sigue protegido por
 *   su guard de rol 1 en el servidor.
 *
 *   ALCANCE EN CADA PETICIÓN. `idevent` e `idsport` viajan SIEMPRE como query params, que es una de
 *   las tres fuentes que el guard acepta (params, query, body) y la única que sirve igual para GET,
 *   DELETE y escrituras. No son un permiso: el guard los verifica contra la base y desde ahí nadie
 *   vuelve a leerlos del cliente.
 *
 *   CONCURRENCIA. Toda escritura del borrador exige su `version`. Se envía en la cabecera `If-Match`,
 *   que el controlador prefiere sobre `body.version`. Una sola forma de decirlo evita que el cliente
 *   mande dos versiones distintas.
 *
 *   ERRORES. No se traducen aquí: se relanza el error de axios tal cual para que la capa de interfaz
 *   lea `code`/`details` del sobre V2 (programmingCenterErrors.js). Un cliente que aplana el error
 *   pierde `PLAN_HASH_CONFLICT` y con él la única forma de no reintentar a ciegas.
 * COLLABORATORS: httpService.js, hooks/useProgrammingCenterStatus.js, hooks/useProgrammingCenterData.js,
 *   utils/programmingCenterErrors.js.
 * ROUTE: winscore/src/services/programmingCenterService.js
 */
import httpService from "./httpService.js";

export const PROGRAMMING_CENTER_ADMIN_BASE = "/api/hayllis/v2/admin/programming-center";
export const PROGRAMMING_CENTER_STATUS_PATH = "/api/hayllis/v2/programming-center/status";

/** Todo apagado: el valor que se devuelve siempre que no haya una confirmación explícita del backend. */
export const PROGRAMMING_CENTER_STATUS_OFF = Object.freeze({
  enabled: false,
  publicEnabled: false,
  hooksEnabled: false,
});

const unwrap = (response) => response?.data?.data ?? {};

export const getProgrammingCenterStatus = async () => {
  try {
    const data = unwrap(await httpService.get(PROGRAMMING_CENTER_STATUS_PATH));
    return {
      enabled: data.enabled === true,
      publicEnabled: data.publicEnabled === true,
      hooksEnabled: data.hooksEnabled === true,
    };
  } catch (error) {
    return { ...PROGRAMMING_CENTER_STATUS_OFF };
  }
};

// ------------------------------------ utilidades internas ------------------------------------

const url = (path) => `${PROGRAMMING_CENTER_ADMIN_BASE}${path}`;

const scope = ({ idevent, idsport }) => ({
  idevent: Number(idevent),
  idsport: Number(idsport),
});

/** `If-Match` solo cuando hay una versión que exigir; el backend distingue "ausente" (400) de "vieja" (409). */
const versionConfig = (version) =>
  Number.isInteger(Number(version)) && Number(version) > 0
    ? { headers: { "If-Match": String(Number(version)) } }
    : {};

const request = async ({ method, path, params, data, version }) => {
  const config = {
    method,
    url: url(path),
    params,
    ...versionConfig(version),
  };
  if (data !== undefined) config.data = data;
  const response = await httpService.request(config);
  return unwrap(response);
};

// ---------------------------------------- lecturas ----------------------------------------

export const getProgrammingCenterContext = ({ idevent, idsport }) =>
  request({ method: "get", path: "/context", params: scope({ idevent, idsport }) });

export const getProgrammingCenterDraft = ({ idevent, idsport }) =>
  request({ method: "get", path: "/draft", params: scope({ idevent, idsport }) });

export const getProgrammingCenterOwnership = ({ idevent, idsport }) =>
  request({ method: "get", path: "/ownership", params: scope({ idevent, idsport }) });

// --------------------------------------- borrador ---------------------------------------

/** Vista previa determinista. No persiste nada: es exactamente lo que guardaría `putDraft`. */
export const generateFixturePreview = ({ idevent, idsport, config }) =>
  request({
    method: "post",
    path: "/generate",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...config },
  });

export const putProgrammingCenterDraft = ({ idevent, idsport, config, version }) =>
  request({
    method: "put",
    path: "/draft",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...config },
    version,
  });

export const previewDraftSync = ({ idevent, idsport, config }) =>
  request({
    method: "post",
    path: "/sync/preview",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...config },
  });

/** El `summaryHash` es el del preview que el administrador está viendo; nunca uno recalculado. */
export const applyDraftSync = ({ idevent, idsport, config, summaryHash, version }) =>
  request({
    method: "post",
    path: "/sync/apply",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...config, summaryHash },
    version,
  });

export const createManualMatch = ({ idevent, idsport, payload, version }) =>
  request({
    method: "post",
    path: "/manual-match",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...payload },
    version,
  });

export const deleteDraftMatch = ({ idevent, idsport, iddraftmatch, version }) =>
  request({
    method: "delete",
    path: `/draft-match/${Number(iddraftmatch)}`,
    params: scope({ idevent, idsport }),
    version,
  });

export const convertDraftMatchToManual = ({ idevent, idsport, iddraftmatch, version }) =>
  request({
    method: "post",
    path: `/draft-match/${Number(iddraftmatch)}/manual`,
    params: scope({ idevent, idsport }),
    data: scope({ idevent, idsport }),
    version,
  });

// -------------------------------------- programación --------------------------------------

/**
 * Lote de programación. `matches: [{ iddraftmatch, date?, time?, idsede?, idcancha? }]`, máximo 500 y
 * atómico: el backend decide por fila si escribe el borrador o el partido materializado (D-08).
 */
export const scheduleBatch = ({ idevent, idsport, matches, version }) =>
  request({
    method: "patch",
    path: "/schedule/batch",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), matches },
    version,
  });

export const scheduleJornada = ({ idevent, idsport, payload, version }) =>
  request({
    method: "patch",
    path: "/schedule/jornada",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...payload },
    version,
  });

// ------------------------------------- materialización -------------------------------------

export const previewMaterialization = ({ idevent, idsport }) =>
  request({
    method: "post",
    path: "/materialize/preview",
    params: scope({ idevent, idsport }),
    data: scope({ idevent, idsport }),
  });

export const materializeDraft = ({ idevent, idsport, options = {}, version }) =>
  request({
    method: "post",
    path: "/materialize",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...options },
    version,
  });

// -------------------------------------- participantes --------------------------------------

export const resolveParticipants = ({ idevent, idsport, clearMissing = false }) =>
  request({
    method: "post",
    path: "/participants/resolve",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), clearMissing: clearMissing === true },
  });

/**
 * "Asignar institución al slot A4". El cliente NO manda participantes a un partido: manda la
 * autoridad del sorteo (institución → grupo/slot) y el backend resuelve los partidos (D-36).
 */
export const assignParticipantToSlot = ({ idevent, idsport, payload }) =>
  request({
    method: "post",
    path: "/participants/assign-slot",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...payload },
  });

// -------------------------------------- reorganización --------------------------------------

/**
 * `confirmRemovals` FORMA PARTE del plan: el `planHash` que devuelve el preview lo incluye. Por eso
 * cambiar la confirmación obliga a volver a pedir el preview, y no se puede aprobar un plan que no
 * retira nada y ejecutar uno que retira tres partidos.
 */
export const previewReorganization = ({ idevent, idsport, config, confirmRemovals = false }) =>
  request({
    method: "post",
    path: "/reorganize/preview",
    params: scope({ idevent, idsport }),
    data: {
      ...scope({ idevent, idsport }),
      ...config,
      confirmRemovals: confirmRemovals === true,
    },
  });

export const applyReorganization = ({
  idevent,
  idsport,
  config,
  planHash,
  confirmRemovals = false,
  version,
}) =>
  request({
    method: "post",
    path: "/reorganize/apply",
    params: scope({ idevent, idsport }),
    data: {
      ...scope({ idevent, idsport }),
      ...config,
      planHash,
      confirmRemovals: confirmRemovals === true,
    },
    version,
  });

/** Retira UN partido propio sin actividad (state = -2). Un externo responde 403 MATCH_NOT_OWNED. */
export const retireCenterMatch = ({ idevent, idsport, idmatch }) =>
  request({
    method: "delete",
    path: `/match/${Number(idmatch)}`,
    params: scope({ idevent, idsport }),
  });

// -------------------------------- grafo de fases (WG5) --------------------------------
//
// El cliente NUNCA manda un `idparticipant` a un partido, tampoco aquí. Pide "recalcula" y, como
// mucho, AUTORIZA lado a lado un reemplazo sobre un partido que ya tiene actividad. Quién entra en
// cada lado lo decide el servidor a partir del resultado del partido de origen.

/** Grafo tal y como está: nodos, lados, estado de cada lado y ciclos. No escribe nada. */
export const getPhaseGraph = ({ idevent, idsport }) =>
  request({ method: "get", path: "/graph", params: scope({ idevent, idsport }) });

/**
 * "Actualizar clasificados". `force` es una lista de `{ idmatch, side }`: la protección de un
 * partido con actividad se levanta LADO A LADO y nunca en bloque (D-29).
 */
export const recomputePhaseGraph = ({
  idevent,
  idsport,
  reason = "manual",
  force = [],
  forcePositions = false,
}) =>
  request({
    method: "post",
    path: "/graph/recompute",
    params: scope({ idevent, idsport }),
    data: {
      ...scope({ idevent, idsport }),
      reason,
      ...(force.length ? { force } : {}),
      ...(forcePositions ? { forcePositions: true } : {}),
    },
  });

/**
 * Edita, bloquea o quita la fuente de UN lado. `locked: true` congela el lado frente a la
 * recomputación; `clear: true` quita la arista; `reference` la define o la cambia.
 */
export const updateGraphSource = ({ idevent, idsport, idmatch, side, reference, locked, clear }) =>
  request({
    method: "patch",
    path: "/graph/sources",
    params: scope({ idevent, idsport }),
    data: {
      ...scope({ idevent, idsport }),
      idmatch: Number(idmatch),
      side: Number(side),
      ...(reference === undefined ? {} : { reference }),
      ...(locked === undefined ? {} : { locked: Boolean(locked) }),
      ...(clear ? { clear: true } : {}),
    },
  });

/**
 * Recomputación DISPARADA DESDE LA PANTALLA DE RESULTADOS, tras oficializar un partido.
 *
 * Es aditiva y, con el gancho del servidor encendido, redundante: se llama igualmente porque ese
 * gancho vive detrás de un flag que puede estar apagado, y porque así quien oficializa ve el cuadro
 * al día sin recargar. NUNCA propaga un error ni enseña uno: quien llena resultados puede no ser
 * rol 1 y el módulo puede estar apagado, y en ambos casos la respuesta correcta es "no pasa nada"
 * — el partido ya quedó oficializado, que es lo que esa pantalla vino a hacer.
 *
 * @returns {Promise<boolean>} `true` si el servidor recomputó; `false` si no pudo y da igual.
 */
export const requestGraphRecomputeAfterOfficialization = async ({ idevent, idsport }) => {
  try {
    await recomputePhaseGraph({ idevent, idsport, reason: "officialization" });
    return true;
  } catch {
    return false;
  }
};

/** "Adoptar en el grafo": única excepción a la propiedad (D-34). Solo inserta aristas. */
export const adoptMatchIntoGraph = ({ idevent, idsport, idmatch, sources }) =>
  request({
    method: "post",
    path: "/graph/adopt",
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), idmatch: Number(idmatch), sources },
  });

// ----------------------------------------- canchas -----------------------------------------

export const listCourts = ({ idevent, idsport, idsede }) =>
  request({
    method: "get",
    path: `/venues/${Number(idsede)}/courts`,
    params: scope({ idevent, idsport }),
  });

export const createCourt = ({ idevent, idsport, idsede, name, sortOrder }) =>
  request({
    method: "post",
    path: `/venues/${Number(idsede)}/courts`,
    params: scope({ idevent, idsport }),
    data: {
      ...scope({ idevent, idsport }),
      name,
      ...(sortOrder === undefined ? {} : { sort_order: sortOrder }),
    },
  });

export const updateCourt = ({ idevent, idsport, idsede, idcancha, changes }) =>
  request({
    method: "patch",
    path: `/venues/${Number(idsede)}/courts/${Number(idcancha)}`,
    params: scope({ idevent, idsport }),
    data: { ...scope({ idevent, idsport }), ...changes },
  });

/** Desactiva (state = 0); nunca borra: los partidos ya programados conservan su cancha. */
export const deactivateCourt = ({ idevent, idsport, idsede, idcancha }) =>
  request({
    method: "delete",
    path: `/venues/${Number(idsede)}/courts/${Number(idcancha)}`,
    params: scope({ idevent, idsport }),
  });

// ------------------------------------ impresión (WG6) ------------------------------------

/**
 * PDF `venue-date-schedule` (SEDE → FECHA → PARTIDOS). Se descarga como binario: la respuesta no es
 * el sobre V2, así que NO pasa por `request`/`unwrap`. El nombre de archivo lo propone el servidor en
 * `Content-Disposition`, que el backend expone con `Access-Control-Expose-Headers`.
 *
 * `source`: `matches` imprime solo lo materializado; `draft` imprime también lo que aún es borrador
 * y el PDF sale marcado "BORRADOR".
 */
export const downloadVenueSchedulePdf = async ({ idevent, idsport, source = "matches" }) => {
  const response = await httpService.request({
    method: "get",
    url: url("/print"),
    params: { ...scope({ idevent, idsport }), layout: "venue", source },
    responseType: "blob",
  });

  const disposition = String(response?.headers?.["content-disposition"] || "");
  const proposed = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = proposed || `programacion_sedes_${Number(idevent)}_${Number(idsport)}.pdf`;

  return { blob: response.data, filename };
};

/** Modelo de impresión en JSON: lo que saldría impreso, sin generar el PDF. */
export const getVenueSchedulePrintModel = ({ idevent, idsport, source = "matches" }) =>
  request({
    method: "get",
    path: "/print/model",
    params: { ...scope({ idevent, idsport }), source },
  });
